export interface ServiceCity {
  placeId: string;
  label: string;
}

export interface ServiceCitiesState {
  sameAsProfile: boolean;
  ownCities: ServiceCity[];
  profileCities: ServiceCity[];
  // What is stored on the server: decides whether a reset has to be sent on save.
  wasFollowingProfile: boolean;
}

export type SameAsProfileChange =
  | { kind: 'apply'; state: ServiceCitiesState }
  | { kind: 'confirm-reset' };

export type ServiceCitiesPayloadResult =
  | { ok: true; payload: { cities?: ServiceCity[]; followProfileCities?: true } }
  | { ok: false; reason: 'own-cities-required' };

interface ServiceCitiesSource {
  cities?: ServiceCity[] | null;
  followsProfileCities?: boolean | null;
  profile?: { cities?: ServiceCity[] | null } | null;
}

function normalizeServiceCities(cities?: ServiceCity[] | null): ServiceCity[] {
  const citiesByPlaceId = new Map<string, ServiceCity>();

  for (const city of cities ?? []) {
    const placeId = city.placeId?.trim() ?? '';
    const label = city.label?.trim() ?? '';
    if (!placeId || !label || citiesByPlaceId.has(placeId)) {
      continue;
    }

    citiesByPlaceId.set(placeId, { placeId, label });
  }

  return Array.from(citiesByPlaceId.values());
}

function hasSameCities(firstCities: ServiceCity[], secondCities: ServiceCity[]): boolean {
  if (firstCities.length !== secondCities.length) {
    return false;
  }

  const secondPlaceIds = new Set(secondCities.map((city) => city.placeId));
  return firstCities.every((city) => secondPlaceIds.has(city.placeId));
}

export function createServiceCitiesState(service?: ServiceCitiesSource | null): ServiceCitiesState {
  const wasFollowingProfile = service?.followsProfileCities !== false;
  const serviceCities = normalizeServiceCities(service?.cities);
  const profileCities = service?.profile?.cities
    ? normalizeServiceCities(service.profile.cities)
    : wasFollowingProfile
      ? serviceCities
      : [];

  return {
    sameAsProfile: wasFollowingProfile,
    ownCities: wasFollowingProfile ? [] : serviceCities,
    profileCities,
    wasFollowingProfile,
  };
}

export function resolveVisibleServiceCities(state: ServiceCitiesState): ServiceCity[] {
  return state.sameAsProfile ? state.profileCities : state.ownCities;
}

export function updateOwnServiceCities(
  state: ServiceCitiesState,
  cities: ServiceCity[]
): ServiceCitiesState {
  if (state.sameAsProfile) {
    return state;
  }

  return { ...state, ownCities: normalizeServiceCities(cities) };
}

export function confirmServiceCitiesReset(state: ServiceCitiesState): ServiceCitiesState {
  return { ...state, sameAsProfile: true, ownCities: [] };
}

export function requestSameAsProfileChange(
  state: ServiceCitiesState,
  nextSameAsProfile: boolean
): SameAsProfileChange {
  if (nextSameAsProfile === state.sameAsProfile) {
    return { kind: 'apply', state };
  }

  if (!nextSameAsProfile) {
    return {
      kind: 'apply',
      state: { ...state, sameAsProfile: false, ownCities: state.profileCities },
    };
  }

  const hasSavedOwnCities = !state.wasFollowingProfile;
  const hasEditedOwnCities =
    state.ownCities.length > 0 && !hasSameCities(state.ownCities, state.profileCities);

  if (hasSavedOwnCities || hasEditedOwnCities) {
    return { kind: 'confirm-reset' };
  }

  return { kind: 'apply', state: confirmServiceCitiesReset(state) };
}

export function buildServiceCitiesPayload(state: ServiceCitiesState): ServiceCitiesPayloadResult {
  if (state.sameAsProfile) {
    return {
      ok: true,
      payload: state.wasFollowingProfile ? {} : { followProfileCities: true },
    };
  }

  const cities = normalizeServiceCities(state.ownCities);
  if (cities.length === 0) {
    return { ok: false, reason: 'own-cities-required' };
  }

  return { ok: true, payload: { cities } };
}
